import * as XLSX from "xlsx";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

import { COLORS } from "../../../constants";
import type { RelatorioSaboresProducaoFilters } from "../../../services/producaoService";

function hexToRgb(hex: string): [number, number, number] {
  const h = hex.replace("#", "");
  return [
    parseInt(h.slice(0, 2), 16),
    parseInt(h.slice(2, 4), 16),
    parseInt(h.slice(4, 6), 16),
  ];
}

function formatISODateToBR(iso: string): string {
  const [y, m, d] = iso.split("-");
  if (!y || !m || !d) return iso;
  return `${d}/${m}/${y}`;
}

function fileDateSuffix(): string {
  return `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, "0")}-${String(new Date().getDate()).padStart(2, "0")}`;
}

export type RelatorioFiltroResumo = { label: string; value: string };

export interface RelatorioSaboresExportInput {
  periodoInicioISO: string;
  periodoFimISO: string;
  filtros: RelatorioFiltroResumo[];
  linhas: Array<{ lojaNome: string; rotulo: string; quantidade: number }>;
  linhasLojaTamanhoSabor: Array<{
    lojaNome: string;
    tamanhoNome: string;
    saborNome: string;
    quantidade: number;
  }>;
  totalQuantidade: number;
  geradoEmLabel: string;
}

type Option = { value: number; label: string };

function formatQuantidade(valor: number): string {
  return valor.toLocaleString("pt-BR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}

export function buildFiltrosResumo(
  applied: RelatorioSaboresProducaoFilters | null,
  lojaOptions: Option[],
  tamanhoOptions: Option[],
  saborOptions: Option[],
  usuarioOptions: Option[]
): RelatorioFiltroResumo[] {
  if (!applied) return [];
  const lojaLabel =
    applied.lojaId != null
      ? (lojaOptions.find((o) => o.value === applied.lojaId)?.label ??
        `ID ${applied.lojaId}`)
      : "Todas";
  const tamanhoLabel =
    applied.tamanhoId != null
      ? (tamanhoOptions.find((o) => o.value === applied.tamanhoId)?.label ??
        `ID ${applied.tamanhoId}`)
      : "Todos";
  const saborLabel =
    applied.saborIds != null && applied.saborIds.length > 0
      ? applied.saborIds
          .map(
            (id) =>
              saborOptions.find((o) => o.value === id)?.label ?? `ID ${id}`
          )
          .join(", ")
      : "Não filtrado por sabor";
  const usuarioLabel =
    applied.usuarioId != null
      ? (usuarioOptions.find((o) => o.value === applied.usuarioId)?.label ??
        `ID ${applied.usuarioId}`)
      : "Todos";
  return [
    { label: "Loja", value: lojaLabel },
    { label: "Tamanho", value: tamanhoLabel },
    { label: "Sabores", value: saborLabel },
    { label: "Pizzaiolo", value: usuarioLabel },
  ];
}

type JsPdfWithAutoTable = jsPDF & {
  lastAutoTable?: { finalY: number };
};

export function exportRelatorioSaboresXlsx(
  input: RelatorioSaboresExportInput
): void {
  const pIni = formatISODateToBR(input.periodoInicioISO);
  const pFim = formatISODateToBR(input.periodoFimISO);
  const rows: (string | number)[][] = [
    ["Dom Belloni"],
    ["Relatório — Pizzas por sabor"],
    [],
    ["Período", `${pIni} a ${pFim}`],
    ...input.filtros.map((f) => [f.label, f.value]),
    [],
    ["Totais por loja, tamanho e sabor (com fragmentação)"],
    ["Loja", "Tamanho", "Sabor", "Quantidade"],
    ...input.linhasLojaTamanhoSabor.map((r) => [
      r.lojaNome,
      r.tamanhoNome,
      r.saborNome,
      formatQuantidade(r.quantidade),
    ]),
    [],
    ["Loja", "Descrição", "Quantidade"],
    ...input.linhas.map((r) => [r.lojaNome, r.rotulo, r.quantidade]),
    [],
    ["Total (soma das linhas)", "", input.totalQuantidade],
    [],
    ["Gerado em", input.geradoEmLabel],
  ];
  const ws = XLSX.utils.aoa_to_sheet(rows);
  ws["!cols"] = [{ wch: 22 }, { wch: 26 }, { wch: 36 }, { wch: 14 }];
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Relatório");
  XLSX.writeFile(wb, `relatorio-pizzas-sabor-${fileDateSuffix()}.xlsx`);
}

export function exportRelatorioSaboresPdf(
  input: RelatorioSaboresExportInput
): void {
  const primary = hexToRgb(COLORS.PRIMARY);
  const primaryDark = hexToRgb(COLORS.PRIMARY_DARK);
  const pIni = formatISODateToBR(input.periodoInicioISO);
  const pFim = formatISODateToBR(input.periodoFimISO);

  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 14;

  doc.setFillColor(primary[0], primary[1], primary[2]);
  doc.rect(0, 0, pageW, 28, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.text("Pizzas por sabor", margin, 12);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.text("Dom Belloni · Produção", margin, 19);

  autoTable(doc, {
    startY: 32,
    body: [
      ["Período", `${pIni}  até  ${pFim}`],
      ...input.filtros.map((f) => [f.label, f.value]),
    ],
    theme: "plain",
    styles: {
      fontSize: 9,
      textColor: [42, 42, 42],
      cellPadding: 2.5,
    },
    columnStyles: {
      0: {
        fontStyle: "bold",
        cellWidth: 36,
      },
      1: { cellWidth: pageW - margin * 2 - 36 },
    },
    margin: { left: margin, right: margin },
  });

  const docExt = doc as JsPdfWithAutoTable;
  const afterMeta = (docExt.lastAutoTable?.finalY ?? 40) + 5;

  autoTable(doc, {
    startY: afterMeta,
    head: [["Loja", "Tamanho", "Sabor", "Qtde."]],
    body: input.linhasLojaTamanhoSabor.map((r) => [
      r.lojaNome,
      r.tamanhoNome,
      r.saborNome,
      formatQuantidade(r.quantidade),
    ]),
    theme: "striped",
    headStyles: {
      fillColor: primary,
      textColor: 255,
      fontStyle: "bold",
      halign: "left",
      valign: "middle",
    },
    columnStyles: {
      0: { cellWidth: 30, halign: "left" },
      1: { cellWidth: 33, halign: "left" },
      2: { cellWidth: pageW - margin * 2 - 30 - 33 - 20, halign: "left" },
      3: { cellWidth: 20, halign: "right" },
    },
    bodyStyles: {
      textColor: [38, 38, 38],
      lineColor: [230, 230, 230],
    },
    alternateRowStyles: {
      fillColor: [255, 252, 251],
    },
    margin: { left: margin, right: margin, bottom: 16 },
    styles: {
      fontSize: 9,
      cellPadding: { top: 3, bottom: 3, left: 2.5, right: 2.5 },
      lineWidth: 0.08,
    },
  });

  const afterLojaTamanhoSabor = (docExt.lastAutoTable?.finalY ?? afterMeta) + 5;

  autoTable(doc, {
    startY: afterLojaTamanhoSabor,
    head: [["Loja", "Descrição", "Qtde."]],
    body: input.linhas.map((r) => [r.lojaNome, r.rotulo, String(r.quantidade)]),
    foot: [["Total (soma das linhas)", "", String(input.totalQuantidade)]],
    theme: "striped",
    headStyles: {
      fillColor: primary,
      textColor: 255,
      fontStyle: "bold",
      halign: "left",
      valign: "middle",
    },
    footStyles: {
      fillColor: primaryDark,
      textColor: 255,
      fontStyle: "bold",
    },
    columnStyles: {
      0: { cellWidth: 32, halign: "left" },
      1: { cellWidth: pageW - margin * 2 - 32 - 22, halign: "left" },
      2: { cellWidth: 22, halign: "right" },
    },
    bodyStyles: {
      textColor: [38, 38, 38],
      lineColor: [230, 230, 230],
    },
    alternateRowStyles: {
      fillColor: [255, 252, 251],
    },
    margin: { left: margin, right: margin, bottom: 16 },
    styles: {
      fontSize: 9,
      cellPadding: { top: 3, bottom: 3, left: 2.5, right: 2.5 },
      lineWidth: 0.08,
    },
  });

  const pages = doc.getNumberOfPages();
  for (let i = 1; i <= pages; i++) {
    doc.setPage(i);
    doc.setFontSize(7.5);
    doc.setTextColor(115, 115, 115);
    doc.setFont("helvetica", "normal");
    doc.text(
      `${input.geradoEmLabel}  ·  Folha ${i} de ${pages}`,
      margin,
      pageH - 6
    );
  }

  doc.save(`relatorio-pizzas-sabor-${fileDateSuffix()}.pdf`);
}
